/**
 * Scanning primitives shared by the three OpenDocument body parsers.
 *
 * ODF is more deeply nested than the OOXML parts this codebase already reads.
 * A `text:span` can contain another `text:span`; a `draw:frame` can contain a
 * text box that contains a frame; an `office:annotation` sits *inside* the
 * paragraph it annotates and holds `text:p` elements of its own. Regular
 * expressions over such markup go wrong in ways that are silent — a comment's
 * text quietly becomes body text, a nested span closes its parent early.
 *
 * So this module provides a real tokenizer instead. It is still a scanner
 * rather than a DOM build: memory stays proportional to the output, and every
 * token carries its absolute byte span, which is what the part-preserving save
 * path needs to rewrite one paragraph and leave the rest of the file alone.
 *
 * The unit conversions live here too. ODF writes lengths as strings with
 * units — `fo:font-size="12pt"`, `svg:width="8.5cm"` — where OOXML writes
 * integers in a fixed unit. Every consumer needs the same conversion, and
 * getting it wrong shows up as a document that renders at the wrong scale
 * rather than as an error.
 */
import { unescapeXml } from '../ooxml/xml.js';

/* ------------------------------------------------------------------ *
 * attributes
 * ------------------------------------------------------------------ */

/**
 * Read one attribute from raw attribute text.
 *
 * Names must be fully qualified. `attr(tag, 'id')` would also match
 * `draw:id="…"`, because `:` ends a word and `\b` therefore sits between them —
 * so every call site in this package passes the prefix.
 */
export function attr(attrs: string, name: string): string | null {
  const found = new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)')`).exec(attrs);
  if (!found) return null;
  return unescapeXml(found[2] ?? found[3] ?? '');
}

export function intAttr(attrs: string, name: string, fallback: number): number {
  const raw = attr(attrs, name);
  if (raw === null) return fallback;
  const value = Number.parseInt(raw, 10);
  return Number.isFinite(value) ? value : fallback;
}

/* ------------------------------------------------------------------ *
 * lengths
 * ------------------------------------------------------------------ */

/** Inches per unit, for the units ODF permits in a length. */
const INCHES: Readonly<Record<string, number>> = {
  in: 1,
  pt: 1 / 72,
  pc: 1 / 6,
  cm: 1 / 2.54,
  mm: 1 / 25.4,
  px: 1 / 96,
  em: 0,       // relative to the current font; not resolvable here
};

const EMU_PER_INCH = 914_400;
const PX_PER_INCH = 96;

/** `"8.5cm"` → inches. Null for a missing, malformed or relative length. */
export function lengthToInches(raw: string | null): number | null {
  if (!raw) return null;
  const m = /^\s*(-?[0-9]*\.?[0-9]+)\s*([a-z]*)\s*$/i.exec(raw);
  if (!m) return null;
  const value = Number(m[1]);
  if (!Number.isFinite(value)) return null;
  const unit = (m[2] || 'in').toLowerCase();
  const perInch = INCHES[unit];
  // An unknown or relative unit is reported as unresolvable rather than being
  // guessed at: a wrong scale is harder to notice than a missing one.
  if (perInch === undefined || perInch === 0) return null;
  return value * perInch;
}

export function lengthToPx(raw: string | null): number | null {
  const inches = lengthToInches(raw);
  return inches === null ? null : inches * PX_PER_INCH;
}

export function lengthToEmu(raw: string | null): number | null {
  const inches = lengthToInches(raw);
  return inches === null ? null : Math.round(inches * EMU_PER_INCH);
}

/* ------------------------------------------------------------------ *
 * tokenizer
 * ------------------------------------------------------------------ */

export type TokenKind = 'open' | 'close' | 'empty' | 'text';

export interface XmlToken {
  readonly kind: TokenKind;
  /** Qualified element name, empty for text. */
  readonly name: string;
  /** Raw attribute text of an `open`/`empty` tag, for {@link attr}. */
  readonly attrs: string;
  /** Decoded character data of a `text` token. */
  readonly text: string;
  readonly start: number;
  readonly end: number;
}

/**
 * Walk `xml` between two offsets, yielding tags and character data.
 *
 * Comments, processing instructions and doctype declarations are skipped;
 * CDATA sections come through as text. Nothing is validated — a malformed
 * document yields what it can and stops, because a viewer that refuses to open
 * a file it could partially read is the wrong trade (§34).
 */
export function* tokenize(xml: string, from = 0, to = xml.length): Generator<XmlToken> {
  let cursor = from;

  while (cursor < to) {
    const lt = xml.indexOf('<', cursor);
    if (lt < 0 || lt >= to) {
      if (cursor < to) {
        const text = xml.slice(cursor, to);
        if (text) yield { kind: 'text', name: '', attrs: '', text: unescapeXml(text), start: cursor, end: to };
      }
      return;
    }

    if (lt > cursor) {
      yield {
        kind: 'text', name: '', attrs: '',
        text: unescapeXml(xml.slice(cursor, lt)),
        start: cursor, end: lt,
      };
    }

    if (xml.startsWith('<!--', lt)) {
      const close = xml.indexOf('-->', lt + 4);
      cursor = close < 0 ? to : close + 3;
      continue;
    }
    if (xml.startsWith('<![CDATA[', lt)) {
      const close = xml.indexOf(']]>', lt + 9);
      const end = close < 0 ? to : close;
      yield {
        kind: 'text', name: '', attrs: '',
        // CDATA is literal by definition; unescaping it would be wrong.
        text: xml.slice(lt + 9, end),
        start: lt, end: close < 0 ? to : close + 3,
      };
      cursor = close < 0 ? to : close + 3;
      continue;
    }
    if (xml.startsWith('<?', lt) || xml.startsWith('<!', lt)) {
      const close = xml.indexOf('>', lt);
      cursor = close < 0 ? to : close + 1;
      continue;
    }

    const tagEnd = findTagEnd(xml, lt);
    if (tagEnd < 0) return;

    const closing = xml[lt + 1] === '/';
    const selfClosing = xml[tagEnd - 1] === '/';
    const inner = xml.slice(lt + (closing ? 2 : 1), selfClosing ? tagEnd - 1 : tagEnd);
    const nameEnd = firstSpace(inner);
    const name = nameEnd < 0 ? inner : inner.slice(0, nameEnd);

    yield {
      kind: closing ? 'close' : selfClosing ? 'empty' : 'open',
      name,
      attrs: nameEnd < 0 ? '' : inner.slice(nameEnd + 1),
      text: '',
      start: lt,
      end: tagEnd + 1,
    };
    cursor = tagEnd + 1;
  }
}

/** Index of the `>` closing the tag that starts at `start`, quote-aware. */
function findTagEnd(xml: string, start: number): number {
  let quote: string | null = null;
  for (let i = start; i < xml.length; i++) {
    const ch = xml[i]!;
    if (quote) {
      if (ch === quote) quote = null;
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === '>') {
      return i;
    }
  }
  return -1;
}

function firstSpace(inner: string): number {
  for (let i = 0; i < inner.length; i++) {
    const ch = inner[i]!;
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') return i;
  }
  return -1;
}

/* ------------------------------------------------------------------ *
 * element spans
 * ------------------------------------------------------------------ */

export interface ElementSpan {
  readonly name: string;
  readonly attrs: string;
  /** Offset of `<`. */
  readonly start: number;
  /** Offset just past `>` of the closing tag. */
  readonly end: number;
  /** Offset just past `>` of the opening tag; equals {@link end} when empty. */
  readonly innerStart: number;
  /** Offset of `<` of the closing tag; equals {@link innerStart} when empty. */
  readonly innerEnd: number;
  readonly selfClosing: boolean;
}

/**
 * The first `name` element at or after `from`, with its full extent.
 *
 * Depth-counting, so a `text:span` inside a `text:span` does not end its
 * parent — which is exactly the bug a `indexOf('</text:span>')` produces, and
 * exactly the shape ODF markup takes.
 */
export function findElement(
  xml: string, name: string, from = 0, to = xml.length,
): ElementSpan | null {
  let depth = 0;
  let open: XmlToken | null = null;

  for (const token of tokenize(xml, from, to)) {
    if (token.kind === 'text') continue;
    if (token.name !== name) continue;

    if (token.kind === 'empty') {
      if (depth > 0) continue;
      return {
        name, attrs: token.attrs,
        start: token.start, end: token.end,
        innerStart: token.end, innerEnd: token.end,
        selfClosing: true,
      };
    }
    if (token.kind === 'open') {
      if (depth === 0) open = token;
      depth += 1;
      continue;
    }
    depth -= 1;
    if (depth === 0 && open) {
      return {
        name, attrs: open.attrs,
        start: open.start, end: token.end,
        innerStart: open.end, innerEnd: token.start,
        selfClosing: false,
      };
    }
    if (depth < 0) return null;
  }
  return null;
}

/** Every direct or nested `name` element between two offsets, in order. */
export function* findElements(
  xml: string, name: string, from = 0, to = xml.length,
): Generator<ElementSpan> {
  let cursor = from;
  for (;;) {
    const span = findElement(xml, name, cursor, to);
    if (!span) return;
    yield span;
    cursor = span.end;
  }
}

/**
 * Children of a span that are one of the given element names.
 *
 * Direct children only: a `table:table-cell` holding a subtable must not have
 * the subtable's cells counted as its own.
 */
export function childElements(
  xml: string, parent: ElementSpan, names: readonly string[],
): ElementSpan[] {
  const wanted = new Set(names);
  const out: ElementSpan[] = [];
  let depth = 0;
  let open: XmlToken | null = null;

  for (const token of tokenize(xml, parent.innerStart, parent.innerEnd)) {
    if (token.kind === 'text') continue;
    if (token.kind === 'empty') {
      if (depth === 0 && wanted.has(token.name)) {
        out.push({
          name: token.name, attrs: token.attrs,
          start: token.start, end: token.end,
          innerStart: token.end, innerEnd: token.end,
          selfClosing: true,
        });
      }
      continue;
    }
    if (token.kind === 'open') {
      if (depth === 0 && wanted.has(token.name)) open = token;
      depth += 1;
      continue;
    }
    depth -= 1;
    if (depth === 0 && open && open.name === token.name) {
      out.push({
        name: open.name, attrs: open.attrs,
        start: open.start, end: token.end,
        innerStart: open.end, innerEnd: token.start,
        selfClosing: false,
      });
      open = null;
    }
  }
  return out;
}
